import React, { createContext, useContext, useEffect, useState } from 'react';
import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import { useTheme } from '@balance-web/theme';

import { Config } from '../../../services/Config';

const GOOGLE_ANALYTICS_SCRIPT_ID = 'platform-google-analytics-script';
const GOOGLE_ANALYTICS_INIT_ID = 'platform-google-analytics-init';

function upsertMeta(name: string, content: string) {
  let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }

  meta.content = content;
}

function upsertFavicon(href: string) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }

  link.href = href;
}

type MetaHeadContextShape = {
  currentPageTitle: string | null;
  setCurrentPageTitle: Dispatch<SetStateAction<string>>;
};
const MetaHeadContext = createContext<MetaHeadContextShape>({
  currentPageTitle: null,
  setCurrentPageTitle: () => {},
});

type MetaHeadProps = PropsWithChildren<{
  appName: string;
  version?: string;
}>;

export function MetaHead({ appName, version, children }: MetaHeadProps) {
  const { colors } = useTheme();
  const [currentPageTitle, setCurrentPageTitle] = useState('');

  useEffect(() => {
    document.title = currentPageTitle
      ? `${appName} / ${currentPageTitle}`
      : appName;
  }, [appName, currentPageTitle]);

  useEffect(() => {
    upsertFavicon(Config.APPLICATION_RECKON_LOGO_URL);
  }, []);

  useEffect(() => {
    upsertMeta('version', version ?? '');
  }, [version]);

  useEffect(() => {
    upsertMeta('msapplication-TileColor', colors.accent);
  }, [colors.accent]);

  useEffect(() => {
    let script = document.getElementById(
      GOOGLE_ANALYTICS_SCRIPT_ID
    ) as HTMLScriptElement | null;

    if (!script) {
      script = document.createElement('script');
      script.id = GOOGLE_ANALYTICS_SCRIPT_ID;
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${Config.APPLICATION_GOOGLE_TAGID}`;
      document.head.appendChild(script);
    }

    let initScript = document.getElementById(
      GOOGLE_ANALYTICS_INIT_ID
    ) as HTMLScriptElement | null;

    if (!initScript) {
      initScript = document.createElement('script');
      initScript.id = GOOGLE_ANALYTICS_INIT_ID;
      document.head.appendChild(initScript);
    }

    initScript.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${Config.APPLICATION_GOOGLE_TAGID}', {
        page_path: window.location.pathname,
      });
    `;
  }, []);

  return (
    <MetaHeadContext.Provider value={{ currentPageTitle, setCurrentPageTitle }}>
      {children}
    </MetaHeadContext.Provider>
  );
}

export function useMetaHead() {
  const context = useContext(MetaHeadContext);
  if (!context) {
    throw new Error(
      'useMetaHead must only be used within children of MetaHead'
    );
  }
  return context;
}
